/**
 * All leases, filterable by property and status.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useList } from '@refinedev/core';
import { LEASE_STATUSES } from 'shared';
import { Avatar } from '../../../components/Avatar.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';
import { useCan } from '../../../lib/capabilities.js';

interface PropertyOption {
  id: string;
  name: string;
}

interface LeaseTenantRow {
  tenantId: string;
  name: string;
  isPrimary: boolean;
  photoUrl: string | null;
}

interface LeaseRow {
  id: string;
  unitLabel: string;
  propertyId: string;
  propertyName: string;
  status: string;
  monthlyRent: string;
  moveOutDate: string | null;
  tenants: LeaseTenantRow[];
}

/**
 * Green/blue/red/neutral, same semantics as the ledger's up/down money
 * coloring: ACTIVE and MONTH_TO_MONTH are both "currently occupied" so they
 * read as positive/informational rather than one being a lesser version of
 * the other; TERMINATED is the one outcome that actually went wrong. DRAFT
 * and ENDED stay the plain neutral pill — neither is good or bad news.
 */
export const STATUS_PILL_CLASS: Partial<Record<string, string>> = {
  ACTIVE: 'pill pill--positive',
  MONTH_TO_MONTH: 'pill pill--info',
  TERMINATED: 'pill pill--negative',
};

export function LeaseList() {
  const navigate = useNavigate();
  const canWrite = useCan('leases:write');
  const [page, setPage] = useState(1);
  const [propertyId, setPropertyId] = useState('');
  const [status, setStatus] = useState('');
  const pageSize = 25;

  const { data: propertiesData } = useList<PropertyOption>({
    resource: 'properties',
    pagination: { current: 1, pageSize: 200 },
  });

  const { data, isLoading, error } = useList<LeaseRow>({
    resource: 'leases',
    pagination: { current: page, pageSize },
    filters: [
      { field: 'propertyId', operator: 'eq', value: propertyId || undefined },
      { field: 'status', operator: 'eq', value: status || undefined },
    ],
  });

  const columns: Column<LeaseRow>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (row) => (
        <>
          <div style={{ fontSize: '1.05rem' }}>{row.unitLabel}</div>
          <div className="muted" style={{ fontStyle: 'italic' }}>
            {row.propertyName}
          </div>
        </>
      ),
    },
    {
      key: 'tenants',
      header: 'Tenants',
      render: (row) =>
        row.tenants.length > 0 ? (
          <div className="stack" style={{ gap: '0.35rem' }}>
            {row.tenants.map((tenant) => (
              <div key={tenant.tenantId} className="row" style={{ gap: '0.5rem' }}>
                <Avatar name={tenant.name} photoUrl={tenant.photoUrl} size="sm" />
                <span>
                  {tenant.name}
                  {tenant.isPrimary ? <span className="muted"> (primary)</span> : null}
                </span>
              </div>
            ))}
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={STATUS_PILL_CLASS[row.status] ?? 'pill'}>{humanizeEnum(row.status)}</span>
      ),
    },
    { key: 'rent', header: 'Rent', numeric: true, render: (row) => `$${row.monthlyRent}` },
    {
      key: 'moveOut',
      header: 'Move-out',
      render: (row) => row.moveOutDate ?? '—',
    },
  ];

  return (
    <section>
      <div className="row">
        <h1>Leases</h1>
        <span className="shell__spacer" />
        {canWrite ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate('/staff/leases/new')}
          >
            New lease
          </button>
        ) : null}
      </div>

      <div className="row">
        <select
          className="field__input"
          value={propertyId}
          onChange={(event) => {
            setPropertyId(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All properties</option>
          {(propertiesData?.data ?? []).map((property) => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>

        <select
          className="field__input"
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
        >
          <option value="">Any status</option>
          {LEASE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {humanizeEnum(s)}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        onRowClick={(row) => void navigate(`/staff/leases/${row.id}`)}
        emptyMessage="No leases yet."
        pagination={{ page, pageSize, total: data?.total ?? 0, onPageChange: setPage }}
      />
    </section>
  );
}
